import React, { useCallback, useEffect, useRef, useState } from 'react';
import Markdown from '../recording/Markdown';
import { announcementsApi, announceChanged, errorOf } from '../../lib/announcements';
import './Announcements.css';

/**
 * IMPORTANT ANNOUNCEMENTS (migration 125), across the top of every page.
 *
 * Under the navbar, beside DegradedBanner (App.js), one strip per Important
 * announcement this person has not acknowledged, until they press "I've
 * read this" - which is kept per person per version on the server, so an
 * edit that asks everybody again brings the strip back. The text is drawn
 * by components/recording/Markdown.js (never as HTML), two lines of it until
 * "Read it all" opens the rest.
 *
 * Asked when it mounts, every minute, when the window comes back into view,
 * and when anything says announcements changed (the bell's tab, the admin
 * page) - so pressing the button in one place clears it everywhere.
 *
 * VIEW AS. An administrator viewing Jarvis as somebody - or previewing a
 * contractor's portal - sees that person's strips, and the button is off
 * with the reason beside it: an acknowledgement is that person's own, and
 * the server refuses one made on their behalf anyway. Before migration 125,
 * or for anybody signed in by neither door, nothing is drawn.
 */
export default function AnnouncementBanner({ token }) {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState({});
  const seq = useRef(0);

  const load = useCallback(() => {
    const n = ++seq.current;
    return announcementsApi.mine(token)
      .then((r) => { if (n === seq.current) setState(r); })
      .catch(() => { if (n === seq.current) setState(null); });
  }, [token]);

  useEffect(() => {
    load();
    const every = setInterval(load, 60000);
    let last = Date.now();
    const looked = () => {
      if (document.visibilityState === 'hidden' || Date.now() - last < 5000) return;
      last = Date.now();
      load();
    };
    window.addEventListener('jarvis:announcements-changed', load);
    window.addEventListener('focus', looked);
    document.addEventListener('visibilitychange', looked);
    return () => {
      clearInterval(every);
      window.removeEventListener('jarvis:announcements-changed', load);
      window.removeEventListener('focus', looked);
      document.removeEventListener('visibilitychange', looked);
    };
  }, [load]);

  const banners = state?.ready ? state.banners || [] : [];
  if (!banners.length) return null;
  const onBehalf = Boolean(state.on_behalf);

  const acknowledge = async (b) => {
    setBusy(b.id);
    setProblem((p) => ({ ...p, [b.id]: '' }));
    try {
      await announcementsApi.acknowledge(b.id, b.version, token);
      setState((s) => ({ ...s, banners: s.banners.filter((x) => x.id !== b.id) }));
      announceChanged();
    } catch (e) {
      setProblem((p) => ({ ...p, [b.id]: errorOf(e, 'That did not save. Try again.') }));
      // A newer version, most likely: show it.
      load();
    }
    setBusy('');
  };

  return (
    <div className="ann-banners" data-testid="announcement-banners">
      {banners.map((b) => {
        const long = String(b.body || '').trim().length > 0;
        const isOpen = Boolean(open[b.id]);
        return (
          <section key={`${b.id}-${b.version}`} className="ann-banner"
                   aria-label={`Important announcement: ${b.title}`} data-announcement={b.id}>
            <span className="ann-banner-mark" aria-hidden="true">!</span>
            <div className="ann-banner-body">
              <p className="ann-banner-title">
                <span className="ann-chip is-important">Important</span>
                <strong>{b.title}</strong>
                {b.edited && <span className="ann-banner-edited">Updated</span>}
              </p>
              {long && (
                <div className={`ann-banner-text${isOpen ? ' is-open' : ''}`} id={`ann-text-${b.id}`}>
                  <Markdown text={b.body} className="ann-md" links />
                </div>
              )}
              {long && (
                <button type="button" className="ann-link" aria-expanded={isOpen} aria-controls={`ann-text-${b.id}`}
                        onClick={() => setOpen((o) => ({ ...o, [b.id]: !o[b.id] }))}>
                  {isOpen ? 'Show less' : 'Read it all'}
                </button>
              )}
              {problem[b.id] && <p className="ann-problem" role="alert">{problem[b.id]}</p>}
              {onBehalf && (
                <p className="ann-behalf" data-testid="announcement-behalf">
                  You are viewing Jarvis as somebody else, so you cannot acknowledge this for them.
                </p>
              )}
            </div>
            <div className="ann-banner-act">
              <button type="button" className="btn btn-primary btn-small" disabled={onBehalf || busy === b.id}
                      onClick={() => acknowledge(b)}>
                {busy === b.id ? 'Saving…' : 'I’ve read this'}
              </button>
            </div>
          </section>
        );
      })}
    </div>
  );
}
